// Links programs on the machine ask to open (gh auth login, an MCP sign-in, a dev server,
// docs). The agent decides where each opens (internal/agent/openurl.go, spec "Links and
// localhost") and tells the panes showing the session. This browser doesn't forward the
// machine's ports, so:
//   - laptop / preview (a link, or a dev server's preview link with the "device" setting):
//     opened here at once when the person just typed (a browser opens a tab only soon after a
//     key or click), else a card with Open (OpenLinks).
//   - machine (a sign-in calling back to the machine, a dev server, or every link with the
//     "machine" setting): already open in the machine's browser. The card says so, with Show
//     desktop (the desktop doesn't come up by itself); a sign-in's stays until it's through.

import { conn } from '../net/connection';
import type { ServerEvent } from '../net/protocol';
import { store, type LinksSetting, type OpenLink } from '../store';
import { updateSettings } from './settingsStore';

const MAX_PER_SESSION = 3;
const DEDUPE_MS = 10_000;
const EXPIRE_MS = 2 * 60_000;
/** A sign-in's card stays until it's through (open-url-done), or this long. */
const SIGNIN_MS = 15 * 60_000;
/** How long "✓ Signed in" stays. */
const DONE_MS = 3000;

type OpenUrlEvent = Extract<ServerEvent, { kind: 'open-url' }>;

/** The session of a link shown on the page in front (Home, Desktop, Settings…: no pane in front), not in a pane. */
export const PAGE = -1;

/** When each link (by session and URL) last came. */
const recent = new Map<string, number>();

const linkOf = (e: OpenUrlEvent, at: number): OpenLink => ({
  id: e.id,
  session: e.session,
  url: e.url,
  target: e.target,
  reason: e.reason ?? (e.target === 'preview' ? 'server' : 'link'), // agents before reasons
  app: e.app ?? '',
  port: e.port,
  path: e.path,
  at,
});

/**
 * A link clicked in a session's terminal goes where a program there opening it would: the agent routes it (the
 * "open" control) and answers with that link's event. The tab opens now, while the click still allows one, and is
 * pointed once the agent answers: at the link, or a dev server's preview link; closed when the link opened in the
 * machine's browser instead (a sign-in calling back to the machine: its card says so). When the agent can't say,
 * the tab gets the link as it is.
 */
export function openClicked(session: number, uri: string) {
  const w = window.open('about:blank', '_blank');
  if (w) w.opener = null;
  conn
    .control<OpenUrlEvent>('open', { session, url: uri }, 5000)
    .then((e) => {
      if (e?.kind !== 'open-url') throw new Error('no route');
      if (e.target === 'machine' || !w) {
        w?.close();
        addOpenLink(e);
        return;
      }
      w.location.href = hereHref(linkOf(e, Date.now()));
    })
    .catch(() => {
      if (w) w.location.href = uri;
      else window.open(uri, '_blank', 'noopener');
    });
}

export function addOpenLink(e: OpenUrlEvent) {
  const now = Date.now();
  const link = linkOf(e, now);
  // The agent sends a link to the device the person used last, whatever it shows: from a session not open here, its
  // card is in the pane in front; with none in front (a page, a phone's Home), on the page, until a pane comes to
  // the front (adoptPageLinks).
  const focused = store.get().focusedSession;
  if (focused === null) link.session = PAGE;
  else if (focused !== link.session && !document.querySelector(`[data-testid=pane][data-session-id="${link.session}"]`)) link.session = focused;
  // The same link again within a few seconds (a program retrying, two opens): keep one, card or tab.
  const key = `${link.session} ${link.url}`;
  if (now - (recent.get(key) ?? -Infinity) < DEDUPE_MS) return;
  recent.set(key, now);
  setTimeout(() => recent.get(key) === now && recent.delete(key), DEDUPE_MS);
  if (link.target !== 'machine' && openHere(link)) return; // opened: nothing to say
  store.set((s) => {
    const mine = s.openLinks.filter((l) => l.session === link.session);
    const drop = new Set(mine.slice(0, Math.max(0, mine.length - (MAX_PER_SESSION - 1))).map((l) => l.id));
    return { openLinks: [...s.openLinks.filter((l) => !drop.has(l.id)), link] };
  });
  setTimeout(() => dismissOpenLink(link.id), isSignin(link) ? SIGNIN_MS : EXPIRE_MS);
}

/** A sign-in waiting in the machine's browser (its card stays until it's through). */
export const isSignin = (l: OpenLink) => l.target === 'machine' && l.reason === 'signin';

/**
 * Opens the link in a new tab of this browser if it allows one now (the person typed or
 * clicked a moment ago); false if not, and the card offers Open instead.
 */
export function openHere(l: OpenLink, allowed = navigator.userActivation?.isActive ?? false): boolean {
  if (!allowed) return false;
  const w = window.open(hereHref(l), '_blank');
  if (!w) return false; // blocked after all
  w.opener = null;
  return true;
}

/** A pane came to the front: the links shown on the page go to it (a sign-in's card keeps waiting there). */
export function adoptPageLinks(session: number) {
  store.set((s) => (s.openLinks.some((l) => l.session === PAGE) ? { openLinks: s.openLinks.map((l) => (l.session === PAGE ? { ...l, session } : l)) } : {}));
}

export function dismissOpenLink(id: string) {
  store.set((s) => (s.openLinks.some((l) => l.id === id) ? { openLinks: s.openLinks.filter((l) => l.id !== id) } : {}));
}

/** The card now says the link is open in the machine's browser (after "Open in dev1's browser"). */
export function markOpenedOnMachine(id: string) {
  store.set((s) => ({ openLinks: s.openLinks.map((l) => (l.id === id ? { ...l, target: 'machine' as const } : l)) }));
}

/** A sign-in in the machine's browser went through: "✓ Signed in", then the card goes. */
export function linkDone(id: string) {
  store.set((s) => ({ openLinks: s.openLinks.map((l) => (l.id === id ? { ...l, done: true, at: Date.now() } : l)) }));
  setTimeout(() => dismissOpenLink(id), DONE_MS);
}

/** Where this browser opens the link: a dev server's preview link, or the link itself. */
export function hereHref(l: OpenLink): string {
  if (l.reason === 'server' && l.port) {
    const q = new URLSearchParams({ port: String(l.port), r: l.path || '/' });
    return `${location.origin}/preview?${q}`;
  }
  return l.url;
}

/** host + path, shortened for the card (the full link is in the title). */
export function shortLink(url: string, max = 56): string {
  let s = url;
  try {
    const u = new URL(url);
    s = u.host + (u.pathname === '/' ? '' : u.pathname);
  } catch {
    /* not a URL: show as is */
  }
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// ---- the links setting ----

let sentLinks: LinksSetting | null = null;

/** This browser tab to the agent: the device a link goes to when it's the one used last (caps "device"). */
const device = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `web-${Math.random().toString(36).slice(2)}`;

/**
 * Tells the agent how this browser takes links (the "caps" control): it doesn't forward the
 * machine's ports, and the person's links setting; that it leaves a session when asked
 * (`cmx detach`); that it takes what's copied on the machine (the "clipboard" event); that
 * it says itself whether a session is in sight (the "viewing" control); and that it follows
 * the machine's workspaces (the "layout" event, useWorkspaceSync). On every (re)connect (force)
 * and whenever the setting changes.
 */
export function sendLinkCaps(force = false) {
  const links = store.get().me?.settings.links ?? 'auto';
  if (!force && links === sentLinks) return;
  sentLinks = links;
  // takesLinks: this connection opens links from any session (addOpenLink), not only those it shows.
  conn.control('caps', { forward: false, links, leaves: true, clipboard: true, viewing: true, layout: true, device, takesLinks: true }).catch(() => {
    sentLinks = null; // not connected: the next connect sends it
  });
}
store.subscribe(() => sendLinkCaps());

/** Saves the links setting for all the person's devices. */
export const setLinksSetting = (links: LinksSetting) => updateSettings({ links });

/** The 'Open links' choices, as Settings and the dashboard word them. */
export const LINK_CHOICES: { value: LinksSetting; label: string; hint: string }[] = [
  {
    value: 'auto',
    label: 'Automatically',
    hint: 'On this computer. From the web, some open on the cloud machine.',
  },
  {
    value: 'device',
    label: 'Always on this computer',
    hint: 'Dev servers too, as a preview link. Some sign-ins still open on the cloud machine.',
  },
  {
    value: 'machine',
    label: 'Always on the cloud machine',
    hint: 'Every link opens on the cloud machine’s desktop.',
  },
];
